import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

/**
 * Toast — إشعار عابر بنفس شكل Toast الخاص بأندرويد.
 * - لا يعيق النقر في الصفحة (pointer-events: none).
 * - يختفي تلقائياً بعد مدة محددة.
 * - يدعم قائمة انتظار (queue) إن تم استدعاؤه أكثر من مرة، تماماً كسلوك أندرويد.
 * - يدعم Light/Dark mode تلقائياً عبر prefers-color-scheme، أو يدوياً عبر
 *   data-theme="dark" | "light" على أي عنصر أب (مثلاً <html>).
 *
 * الاستخدام:
 *   1) لف التطبيق مرة واحدة:
 *        <ToastProvider>
 *          <App />
 *        </ToastProvider>
 *
 *   2) استخدم الـ hook من أي مكون فرعي:
 *        const showToast = useToast();
 *        showToast('تم الحفظ بنجاح');
 *        showToast('حدث خطأ ما', { duration: 'long' }); // أو duration بالميلي ثانية مباشرة
 */

const ToastContext = createContext(null);

const DURATIONS = {
  short: 2000, // Toast.LENGTH_SHORT
  long: 3500,  // Toast.LENGTH_LONG
};

const EXIT_ANIMATION_MS = 250;

let idCounter = 0;

export const ToastProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);

  const showTimeoutRef = useRef(null);
  const exitTimeoutRef = useRef(null);

  const showToast = useCallback((message, options = {}) => {
    if (!message) return;
    const duration =
      typeof options.duration === 'number'
        ? options.duration
        : DURATIONS[options.duration] || DURATIONS.short;

    const id = ++idCounter;
    setQueue((q) => [...q, { id, message, duration }]);
  }, []);

  // أخرج التالي من قائمة الانتظار عندما تكون الحاوية فارغة
  useEffect(() => {
    if (!current && queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrent(next);
      setQueue(rest);
    }
  }, [queue, current]);

  // شغّل دورة حياة الـ toast الحالي: ظهور -> بقاء -> اختفاء -> إزالة
  useEffect(() => {
    if (!current) return undefined;

    setVisible(false);
    const enterRaf = requestAnimationFrame(() => setVisible(true));

    showTimeoutRef.current = setTimeout(() => {
      setVisible(false);
      exitTimeoutRef.current = setTimeout(() => {
        setCurrent(null);
      }, EXIT_ANIMATION_MS);
    }, current.duration);

    return () => {
      cancelAnimationFrame(enterRaf);
      clearTimeout(showTimeoutRef.current);
      clearTimeout(exitTimeoutRef.current);
    };
  }, [current]);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {current &&
        createPortal(
          <div className="toast-layer" role="status" aria-live="polite" aria-atomic="true">
            <div className={`toast-pill ${visible ? 'toast-visible' : 'toast-hidden'}`}>
              {current.message}
            </div>
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast يجب أن يُستخدم داخل <ToastProvider>');
  }
  return ctx;
};

export default ToastProvider;